import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import { RootState } from "../../../../../stores/store";
import { DeliveryAddressResponse } from "../../../../../model/delivery-address";
import { useLazyGetDeliveryAddressesQuery } from "../../../../../service/delivery-address-service";
import { useCreatePayAsYouGoOrderMutation } from "../../../../../service/order-service";
import { HalfCircleCheck } from "../../../../../assets/icons";
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout";
import { CustomerLoader } from "../../../components";
import styles from "./billing-information-page.module.scss";
import { PaymentPlan } from "../../../../../service/contract/solar-recommendation";

type Props = {
  onCancel: () => void;
  onAddAddress: () => void;
  onEditAddress?: (address: DeliveryAddressResponse) => void;
  onOrderSuccess: () => void;
};

export const BillingInformationPage: React.FC<Props> = ({ onCancel, onAddAddress, onEditAddress, onOrderSuccess }) => {
  const selectedProduct = useSelector((state: RootState) => state.getSolar.selectedProduct);
  const recommendationDetails = useSelector((state: RootState) => state.getSolar.recommendationDetails);

  const [getDeliveryAddresses, { isLoading: loadingAddresses, isFetching }] = useLazyGetDeliveryAddressesQuery();
  const [createPayAsYouGoOrder, { isLoading: isSubmitting }] = useCreatePayAsYouGoOrderMutation();

  const [addresses, setAddresses] = useState<DeliveryAddressResponse[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<DeliveryAddressResponse | null>(null);
  const fetchAddresses = useCallback(() => {
    getDeliveryAddresses()
      .unwrap()
      .then((res) => {
        setAddresses(res);
        const defaultAddr = res.find((a) => a.isDefault);
        if (defaultAddr) {
          setSelectedAddress((prev) => prev ?? defaultAddr);
        }
      })
      .catch((err) => { });
  }, [getDeliveryAddresses]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleSelectAddress = (address: DeliveryAddressResponse) => {
    setSelectedAddress(address);
  };

  const handleEditAddress = (address: DeliveryAddressResponse) => {
    if (onEditAddress) {
      onEditAddress(address);
    }
  };

  const handleAcceptRecommendation = async () => {
    if (!selectedAddress || !selectedProduct || !recommendationDetails) return;

    if (recommendationDetails.assessmentDetails?.paymentPlan === PaymentPlan.PayAsYouGo) {
      try {
        await createPayAsYouGoOrder({
          productId: selectedProduct.id,
          recommendationId: recommendationDetails.id,
          deliveryAddressId: selectedAddress.id,
        }).unwrap();
        onOrderSuccess();
      } catch (error: any) {
        toast.error(error?.data?.title || "Failed to place order. Please try again.");
        return;
      }
      
    } else {
      // Continue with standard order flow for other payment plans (choose payment page)
    }
  };

  if (isSubmitting) {
    return (
      <CustomerPageLayout>
        <CustomerLoader
          title="Placing your order"
          subtitle="Please wait while we process your request..."
        />
      </CustomerPageLayout>
    );
  }

  return (
    <>
      <div className={styles.wrapper}>
        <div>
          <span className={styles.title}>Billing Information</span>
          <p className={styles.subtitle}>Manage your billing information</p>
        </div>

        <h3 className={styles.sectionTitle}>Customer Address &amp; Contact</h3>

        {(loadingAddresses || isFetching) ? (
          <div className={styles.loadingContainer}>
            <BounceLoader color="var(--customer-primary)" size={45} />
          </div>
        ) : (
          <div className={styles.addressList}>
            {addresses.map((address) => (
              <div
                key={address.id}
                className={`${styles.addressCard} ${selectedAddress?.id === address.id ? styles.selected : ""}`}
                onClick={() => handleSelectAddress(address)}
              >
                {selectedAddress?.id === address.id && (
                  <span className={styles.checkmark}>
                    <HalfCircleCheck />
                  </span>
                )}

                {address.isDefault && (
                  <span className={styles.defaultBadge}>Default Address</span>
                )}

                <p className={styles.addressRegion}>
                  {address.deliveryAddressInformation.city}, {address.deliveryAddressInformation.region}
                </p>

                <p className={styles.addressStreet}>
                  {address.deliveryAddressInformation.address}
                </p>

                <p className={styles.addressContact}>
                  {address.deliveryAddressInformation.phoneNumber}
                  {address.deliveryAddressInformation.email && `, ${address.deliveryAddressInformation.email}`}
                </p>

                <button
                  className={styles.editButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditAddress(address);
                  }}
                >
                  Edit Address
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          className={styles.addAddressButton}
          onClick={onAddAddress}
        >
          + Add Address &amp; Contact
        </button>

        <div className={styles.actionBar}>
          <button className={styles.cancelButton} onClick={onCancel}>
            Cancel
          </button>
          <button
            className={styles.acceptButton}
            onClick={handleAcceptRecommendation}
            disabled={!selectedAddress}
          >
            Accept Recommendation
          </button>
        </div>
      </div>
    </>
  );
};
